import React, { useCallback, useEffect, useRef, useState } from "react";

import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
// import AspectRatioIcon from "@mui/icons-material/AspectRatio";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import {
  Box,
  debounce,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  StackProps
} from "@mui/material";
import { ReactComponent as OpenFullIcon } from "assets/icons/open-full.svg";
import { DealRoleGuard } from "components/guards";
import { useModals } from "hooks";
import { useDealActions, useDocumentsActions, usePickUserPermissions } from "store";
import { DocumentSectionImage, UserPermissionType } from "store/types";

import { RoleName } from "../../../constants";
import { useContentContext } from "../Content/content-context";

interface ImageSectionProps {
  section: DocumentSectionImage | null;
  onDelete: () => void;
  onFocus?: () => void;
}
export const ImageSection = ({ section, onDelete, onFocus }: ImageSectionProps) => {
  if (!section) return null;

  const myDeals = usePickUserPermissions("deals");
  const noAccessPermission = myDeals && myDeals === UserPermissionType.NO_ACCESS;
  const isViewerPermission = myDeals && myDeals === UserPermissionType.VIEWER;

  const showResizeControls = !noAccessPermission && !isViewerPermission;

  const contentRef: React.RefObject<HTMLDivElement> = useRef(null);
  const isResizing = useRef<boolean>(false);
  const [width, setWidth] = useState<number>(0);
  const [aspectRatio, setAspectRatio] = useState<number | null>(null);
  const [isResizeVisible, setResizeVisible] = useState(0);
  const [alignment, setAlignment] = useState<"left" | "center" | "right">(section.parameters?.alignment || "center");

  const { downloadFileAction } = useDealActions();
  const { downloadDocumentFileAction } = useDocumentsActions();
  const [isOpen, setIsOpen] = useState(false);
  const ref: React.MutableRefObject<SVGSVGElement | null> = useRef(null);
  const { openImageModal } = useModals();
  const { updateSection } = useContentContext();
  const stackStyles: StackProps["sx"] = {
    padding: "1px 1px",
    width: "28px",
    height: "24px",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: "9px",
    borderRadius: "5px",
    // // position: "absolute",
    // top: "10px",
    // right: "10px",
    zIndex: "1000",
    backgroundColor: "#ffffff"
  };

  const handleOpenMenu = (e: React.MouseEvent) => {
    e.stopPropagation();
    ref.current = e.target as SVGSVGElement;
    setIsOpen(true);
  };

  const onDownload = () => {
    const { file } = section;
    if (!file) return;
    console.log(file);

    if (file.deal_id) {
      downloadFileAction({ fileId: file.id, fileName: file.name, dealId: file.deal_id });
    } else {
      file.document_id && downloadDocumentFileAction({ documentId: file.document_id, fileId: file.id });
    }
    setIsOpen(false);
  };

  const debouncedOnResize = useCallback(
    debounce((newWidth: number, newHeight: number) => {
      if (updateSection) {
        updateSection({
          id: section.id,
          parameters: { height: newHeight, width: newWidth, alignment: section.parameters?.alignment }
        });
      }
    }, 500),
    [updateSection, section]
  );

  const debouncedOnAlignment = useCallback(
    debounce((newAlignment: "left" | "center" | "right") => {
      if (updateSection) {
        updateSection({
          id: section.id,
          parameters: { height: section.parameters?.height, width: section.parameters?.width, alignment: newAlignment }
        });
      }
    }, 500),
    [updateSection, section]
  );

  const handleAlignmentChange = (newAlignment: "left" | "center" | "right") => {
    setAlignment(newAlignment);
    debouncedOnAlignment(newAlignment);
  };

  const handleResize = useCallback(
    (event: MouseEvent, corner: string) => {
      if (!isResizing.current || !contentRef.current) return;

      const containerRect = contentRef.current.getBoundingClientRect();
      let newWidth = width;
      let newHeight = width / (aspectRatio || 1);

      switch (corner) {
        case "se":
          newWidth = Math.max(120, Math.min(event.clientX - containerRect.left, 800));
          newHeight = Math.max(120, Math.min(event.clientY - containerRect.top, 800));
          break;
        case "sw":
          newWidth = Math.max(120, Math.min(containerRect.right - event.clientX, 800));
          newHeight = Math.max(120, Math.min(event.clientY - containerRect.top, 800));
          break;
        case "ne":
          newWidth = Math.max(120, Math.min(event.clientX - containerRect.left, 800));
          newHeight = Math.max(120, Math.min(containerRect.bottom - event.clientY, 800));
          break;
        case "nw":
          newWidth = Math.max(120, Math.min(containerRect.right - event.clientX, 800));
          newHeight = Math.max(120, Math.min(containerRect.bottom - event.clientY, 800));
          break;
      }

      if (event.shiftKey && aspectRatio) {
        newWidth = Math.min(newWidth, newHeight * aspectRatio);
        newHeight = newWidth / aspectRatio;
      }

      setWidth(newWidth);
      if (debouncedOnResize) {
        debouncedOnResize(newWidth, newHeight);
      }
    },
    [aspectRatio, debouncedOnResize, width]
  );

  const [activeCorner, setActiveCorner] = useState<string | null>(null);

  const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const img = event.currentTarget;
    const naturalWidth = img.naturalWidth;
    const naturalHeight = img.naturalHeight;
    setWidth(section.parameters?.width ? section.parameters.width : naturalWidth);
    setAspectRatio(
      section.parameters?.height
        ? (section.parameters?.width || naturalWidth) / section.parameters?.height
        : naturalWidth / naturalHeight
    );
  };

  useEffect(() => {
    if (isResizing.current && activeCorner) {
      const handleMouseMove = (e: MouseEvent) => handleResize(e, activeCorner);

      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener(
        "mouseup",
        () => {
          isResizing.current = false;
          setActiveCorner(null);
          window.removeEventListener("mousemove", handleMouseMove);
        },
        { once: true }
      );

      return () => window.removeEventListener("mousemove", handleMouseMove);
    }
  }, [isResizing.current, activeCorner, handleResize]);

  return (
    <Stack
      onMouseEnter={() => showResizeControls && setResizeVisible(1)}
      onMouseLeave={() => setResizeVisible(0)}
      onFocus={onFocus}
      tabIndex={0}
      sx={{
        flexDirection: "column",
        position: "relative",
        gap: "2px",
        flexGrow: 1,
        width: "100%",
        "&:hover": {
          ".MuiStack-root": {
            opacity: showResizeControls ? "1" : "0"
          }
        }
      }}
    >
      {!section.file?.file_url ? (
        <Skeleton sx={{ bgcolor: "#b2bec3" }} variant="rectangular" width="100%" height={160} />
      ) : (
        <Box
          sx={{
            padding: "4px",
            width: "100%",
            display: "flex",
            justifyContent: alignment === "left" ? "flex-start" : alignment === "right" ? "flex-end" : "center"
          }}
        >
          <Box
            ref={contentRef}
            sx={{
              position: "relative",
              overflow: "visible",
              width: `${width}px`,
              // height: aspectRatio && `${Math.round(width / aspectRatio)}px`,
              userSelect: "none",
              borderRadius: "5px",
              maxWidth: "800px"
              // maxHeight: aspectRatio && `${Math.round(800 / aspectRatio)}px`
            }}
          >
            <img
              src={section.file?.file_url}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain"
              }}
              onLoad={handleImageLoad}
            />
            {["nw", "ne", "sw", "se"].map((position) => (
              <Box
                key={position}
                onMouseDown={(e) => {
                  e.preventDefault();
                  isResizing.current = true;
                  setActiveCorner(position);
                }}
                sx={{
                  position: "absolute",
                  width: "12px",
                  height: "12px",
                  background: "#0a3142",
                  opacity: isResizeVisible,
                  cursor: `${position}-resize`,
                  ...(position.includes("n") ? { top: "-3px" } : { bottom: "-3px" }),
                  ...(position.includes("w") ? { left: "-6px" } : { right: "-6px" }),
                  borderRadius: "50%",
                  zIndex: 1000,
                  transition: "all 0.2s",
                  "&:hover": {
                    transform: "scale(1.2)",
                    boxShadow: "0 0 0 2px white, 0 0 0 4px #0a3142"
                  }
                }}
              />
            ))}
            <Box
              sx={{
                position: "absolute",
                top: "10px",
                right: "10px",
                // left: "50%",
                // transform: "translateX(-50%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1,
                backgroundColor: "#ffffff",
                borderRadius: "5px",
                opacity: isResizeVisible,
                padding: "2px 4px"
              }}
            >
              <Stack direction="row" gap="3px" alignItems="center" justifyContent="center">
                <Stack
                  direction="row"
                  sx={{
                    padding: "2px 4px",
                    opacity: isResizeVisible,
                    transition: "opacity 0.2s ease-in-out",
                    zIndex: 1
                  }}
                >
                  <IconButton
                    size="sm"
                    onClick={() => handleAlignmentChange("left")}
                    sx={{
                      padding: "4px",
                      minWidth: "24px",
                      height: "24px",
                      color: alignment === "left" ? "#0a3142" : "#D3D3D3"
                    }}
                  >
                    <FormatAlignLeftIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                  <IconButton
                    size="sm"
                    onClick={() => handleAlignmentChange("center")}
                    sx={{
                      padding: "4px",
                      minWidth: "24px",
                      height: "24px",
                      color: alignment === "center" ? "#0a3142" : "#D3D3D3"
                    }}
                  >
                    <FormatAlignCenterIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                  <IconButton
                    size="sm"
                    onClick={() => handleAlignmentChange("right")}
                    sx={{
                      padding: "4px",
                      minWidth: "24px",
                      height: "24px",
                      color: alignment === "right" ? "#0a3142" : "#D3D3D3"
                    }}
                  >
                    <FormatAlignRightIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Stack>
                {section.file?.file_url && (
                  <Stack sx={{ ...stackStyles, opacity: isOpen ? "1 !important" : "0" }}>
                    <DealRoleGuard availableRoles={[RoleName.Admin, RoleName.Analyst, RoleName.Guest]}>
                      <IconButton
                        sx={{ padding: 0, width: "20px", height: "20px", color: "#5d7782" }}
                        onClick={(e) => handleOpenMenu(e)}
                      >
                        <MoreHorizIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </DealRoleGuard>
                    <Menu anchorEl={ref.current} open={isOpen} onClose={() => setIsOpen(false)}>
                      <MenuItem sx={{ "&:hover": { backgroundColor: "#e4eaf4" } }} onClick={onDownload}>
                        <ListItemIcon>
                          <FileDownloadOutlinedIcon sx={{ color: "#0a3142" }} fontSize="md" />
                        </ListItemIcon>
                        <ListItemText>Download</ListItemText>
                      </MenuItem>
                      <MenuItem
                        sx={{ "&:hover": { backgroundColor: "#e4eaf4" } }}
                        onClick={() => section.file?.file_url && openImageModal({ src: section.file.file_url })}
                      >
                        <ListItemIcon>
                          <OpenFullIcon className="scale-click" />
                        </ListItemIcon>
                        <ListItemText>Fullscreen</ListItemText>
                      </MenuItem>
                      <DealRoleGuard availableRoles={[RoleName.Admin, RoleName.Analyst]}>
                        <MenuItem sx={{ "&:hover": { backgroundColor: "#e4eaf4" } }} onClick={onDelete}>
                          <ListItemIcon>
                            <DeleteOutlineIcon sx={{ color: "#0a3142" }} fontSize="md" />
                          </ListItemIcon>
                          <ListItemText>Delete</ListItemText>
                        </MenuItem>
                      </DealRoleGuard>
                    </Menu>
                  </Stack>
                )}
              </Stack>
            </Box>
          </Box>
        </Box>
      )}
    </Stack>
  );
};
